import React, { useState, useMemo } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  BookOpenIcon,
  AlertTriangleIcon,
  ShieldIcon,
  TrashIcon,
  KeyIcon,
  UserIcon,
  MailIcon,
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FilterIcon,
  HashIcon,
  CreditCardIcon,
  SettingsIcon,
  XIcon,
  ClockIcon,
  LockIcon,
  PackageIcon,
  ActivityIcon,
  MapIcon,
  TypeIcon,
  EyeIcon,
  SendIcon
} from 'lucide-react';

const UltraPlatformRules = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedRules, setExpandedRules] = useState({});

  // Define categories
  const categories = [
    { id: 'all', name: 'Tüm Kurallar', icon: BookOpenIcon, color: 'bg-gray-100 text-gray-700' },
    { id: 'developments', name: 'Geliştirmeler', icon: ActivityIcon, color: 'bg-green-100 text-green-700' },
    { id: 'advance-closing', name: 'Avans Kapama Statüleri', icon: ClockIcon, color: 'bg-yellow-100 text-yellow-700' },
    { id: 'account', name: 'Hesap Yönetimi', icon: TrashIcon, color: 'bg-red-100 text-red-700' },
    { id: 'security', name: 'Güvenlik', icon: ShieldIcon, color: 'bg-blue-100 text-blue-700' },
    { id: 'subscription', name: 'Abonelik Yönetimi', icon: CreditCardIcon, color: 'bg-orange-100 text-orange-700' },
    { id: 'creditcard', name: 'Kredi Kartı İşlemleri', icon: CreditCardIcon, color: 'bg-pink-100 text-pink-700' },
    { id: 'dashboard', name: 'Dashboard', icon: SettingsIcon, color: 'bg-teal-100 teal-700' },
    { id: 'invite', name: 'Davet Sistemi', icon: MailIcon, color: 'bg-purple-100 text-purple-700' },
    { id: 'numbering', name: 'Numaralandırma', icon: HashIcon, color: 'bg-green-100 text-green-700' },
    { id: 'user', name: 'Kullanıcı', icon: UserIcon, color: 'bg-indigo-100 text-indigo-700' },
  ];

  // Define all rules with categories
  const allRules = [
    // ═══════════════════════════════════════════════════════════════════
    // ⏰ AVANS KAPAMA STATÜLERİ (ADVANCE CLOSING STATUSES)
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'advance-closing-status-flow',
      category: 'advance-closing',
      title: '📋 Avans Kapama Status Akışı',
      description: 'Avans talebi oluşturulduktan sonra hangi statülerden geçer ve her statü ne anlama gelir?',
      icon: ClockIcon,
      iconColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-l-yellow-500',
      content: (
        <div className="text-sm text-gray-700 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="font-bold text-blue-800">ℹ️ STATUS AKIŞI</p>
            <p className="mt-2">Bir avans talebi oluşturulduktan sonra aşağıdaki statülerden geçer:</p>
          </div>

          <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">1️⃣</span>
                <p className="font-bold text-yellow-900">Onay Bekliyor (pending)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Kullanıcı avans talebini oluşturduğunda ilk status</li>
                <li>Yönetici onayı bekleniyor</li>
                <li><strong>Badge:</strong> Sarı renk, saat ikonu</li>
              </ul>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">2️⃣</span>
                <p className="font-bold text-purple-900">Ödeme Bekliyor (approved)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Yönetici talebi onayladı</li>
                <li>Finans departmanının ödeme yapması bekleniyor</li>
                <li><strong>Badge:</strong> Mor renk, onay ikonu</li>
              </ul>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">3️⃣</span>
                <p className="font-bold text-blue-900">Kapama Bekliyor (paid)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Ödeme yapıldı</li>
                <li>Kullanıcının masrafları girerek avansı kapatması bekleniyor</li>
                <li><strong>Badge:</strong> Mavi renk, kredi kartı ikonu</li>
                <li><strong>Not:</strong> Eğer <code>closing_status</code> yoksa veya submitted değilse</li>
              </ul>
            </div>

            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">4️⃣</span>
                <p className="font-bold text-orange-900">Finans Onayında (paid + closing_status='submitted')</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Kullanıcı masraflarını girdi ve kapatma talebi gönderdi</li>
                <li>Finans departmanının incelemesi bekleniyor</li>
                <li><strong>Badge:</strong> Turuncu renk, saat ikonu</li>
                <li><strong>Teknik:</strong> <code>status='paid'</code> ve <code>closing_status='submitted'</code></li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">5️⃣</span>
                <p className="font-bold text-gray-900">Kapatıldı (closed)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Finans departmanı masrafları onayladı</li>
                <li>Avans süreci tamamlandı (final status)</li>
                <li><strong>Badge:</strong> Gri renk, kilit ikonu</li>
                <li><strong>Not:</strong> Bu status'ten sonra avans değiştirilemez</li>
              </ul>
            </div>

            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">❌</span>
                <p className="font-bold text-red-900">Reddedildi (rejected)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Yönetici veya finans departmanı talebi reddetti</li>
                <li><strong>Badge:</strong> Kırmızı renk, X ikonu</li>
                <li><strong>Alt status:</strong> Eğer <code>closing_status='rejected'</code> ise "Finans Reddetti" gösterilir</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded mt-4">
            <p className="font-bold text-green-800">✅ UYGULANAN ÇÖZÜM</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Frontend:</strong> AllRequests.js'de <code>getStatusBadge()</code> fonksiyonu güncellendi</li>
              <li><strong>Yeni mantık:</strong> Status akışı yukarıdaki sıraya göre çalışıyor</li>
              <li><strong>Badge renkleri:</strong> Her status için belirgin ve farklı renkler kullanılıyor</li>
            </ul>
          </div>
        </div>
      ),
    },
    // ═══════════════════════════════════════════════════════════════════
    // 🚀 GELİŞTİRMELER (DEVELOPMENTS)
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'dev-case-1',
      category: 'developments',
      title: '🎯 CASE 1: Kredi Kartı Ekstresi - Ödeme Tespiti ve Küsurat Kaybı Sorunu',
      description: 'Garanti ekstresinde ödemeler görünmüyor, tüm tutarlar .00 ile bitiyor (küsuratlar kaybolmuş).',
      icon: ActivityIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-500',
      content: (
        <div className="text-sm text-gray-700 space-y-4">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
            <p className="font-bold text-yellow-800">⚠️ SORUN TANIMI</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Sorun 1:</strong> Garanti ekstresinde ödemeler görünmüyor (Kuveyt'te görünüyordu)</li>
              <li><strong>Sorun 2:</strong> Tüm tutarlar küsuratları kaybediyor (4.466,50 → 4.466,00)</li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">🔍 KÖK NEDEN ANALİZİ</p>
            <div className="space-y-2 ml-4">
              <p><strong>Sorun 1 - Ödeme Tespiti:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Backend keyword-based detection yapıyor ve <code className="bg-gray-200 px-1">type: "payment"</code> set ediyor ✅</li>
                <li>Ancak frontend hala <code className="bg-gray-200 px-1">txn.amount &lt; 0</code> kontrolü kullanıyor ❌</li>
                <li>Garanti ödemelerinde <code className="bg-gray-200 px-1">type === "payment"</code> var ama frontend bunu görmüyor</li>
              </ul>
              
              <p className="mt-3"><strong>Sorun 2 - Küsurat Kaybı:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>AI, transaction amounts'ları integer olarak parse ediyor</li>
                <li>PDF: "4.466,50 TL" → AI Response: 4466 (küsurat kaybolmuş!) ❌</li>
                <li>statement_amount doğru parse ediliyor (409208.73 ✅) ama transactions array'inde kayboluyor</li>
              </ul>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">✅ UYGULANAN ÇÖZÜMLER</p>
            
            <div className="space-y-3 ml-4">
              <div className="bg-blue-50 p-3 rounded">
                <p className="font-semibold text-blue-900">1️⃣ Ödeme Tespiti Düzeltmesi</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
                  <li>Frontend'de <code className="bg-gray-200 px-1">txn.amount &lt; 0</code> kontrolü kaldırıldı</li>
                  <li><code className="bg-gray-200 px-1">txn.type === "payment"</code> filtresi uygulandı</li>
                  <li>Artık işaret değil, keyword'e bakıyor (Rule Engine)</li>
                  <li>3 yerde güncelleme: harcamalar, ödemeler, separator</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-3 rounded">
                <p className="font-semibold text-purple-900">2️⃣ Cents-Based Storage Sistemi</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
                  <li><strong>Backend:</strong> <code className="bg-gray-200 px-1">normalize_amount_tr()</code> fonksiyonu eklendi</li>
                  <li>Tutarlar integer cents olarak saklanıyor (4.466,50 → 446650 cents)</li>
                  <li>MongoDB Schema: <code className="bg-gray-200 px-1">amount_cents</code> (int), <code className="bg-gray-200 px-1">amount_display</code> (string), <code className="bg-gray-200 px-1">amount</code> (float - legacy)</li>
                  <li><strong>Frontend:</strong> <code className="bg-gray-200 px-1">AmountCell</code> component oluşturuldu</li>
                  <li>String render → Grid auto-format yapamaz!</li>
                </ul>
              </div>

              <div className="bg-green-50 p-3 rounded">
                <p className="font-semibold text-green-900">3️⃣ Rule-Based Payment Classification</p>
                <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
                  <li>AI'dan bağımsız, keyword-based tespit</li>
                  <li>10 banka için özel JSON kuralları (<code className="bg-gray-200 px-1">/backend/rules/credit_card/payments/*.json</code>)</li>
                  <li>78 TR/EN anahtar kelime (ödeme, teşekkür, payment, etc.)</li>
                  <li>İşaret kontrolü yok - sadece keyword!</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">📂 DEĞİŞEN DOSYALAR</p>
            <div className="bg-gray-50 p-3 rounded text-xs font-mono space-y-1">
              <p>✅ <strong>Backend:</strong></p>
              <p className="ml-4">- /app/backend/server.py (normalize_amount_tr, cents storage)</p>
              <p className="ml-4">- /app/backend/rules/credit_card/payments/*.json (10 banka kuralı)</p>
              <p></p>
              <p>✅ <strong>Frontend:</strong></p>
              <p className="ml-4">- /app/frontend/src/components/AmountCell.js (yeni component)</p>
              <p className="ml-4">- /app/frontend/src/components/CreditCardManagement.js (type-based filter)</p>
              <p className="ml-4">- /app/frontend/src/utils/formatAmount.js (TR formatter)</p>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">🎯 SONUÇ</p>
            <div className="bg-green-100 border-l-4 border-green-500 p-3 rounded">
              <ul className="list-disc list-inside space-y-1">
                <li>✅ Garanti ödemeleri artık görünüyor</li>
                <li>✅ Küsuratlar korunuyor (cents-based storage)</li>
                <li>✅ Float precision hatası yok</li>
                <li>✅ Her banka formatı destekleniyor</li>
                <li>✅ Keyword-based payment detection çalışıyor</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="font-bold text-blue-800">💡 ÖNEMLİ NOTLAR</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
              <li>Eski veriler legacy <code className="bg-gray-200 px-1">amount</code> field kullanıyor</li>
              <li>Yeni veriler <code className="bg-gray-200 px-1">amount_cents</code> ve <code className="bg-gray-200 px-1">amount_display</code> kullanıyor</li>
              <li>AmountCell her iki durumu da destekliyor (backward compatible)</li>
              <li>AI hala integer döndürebilir - bu durumda .00 ekleniyor (gerçek küsurat kayıp)</li>
            </ul>
          </div>

          <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded">
            <p className="font-bold text-red-800">⚠️ SORUN YAŞANIRSA</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
              <li>AI response loglarını kontrol et: <code className="bg-gray-200 px-1">tail -f /var/log/supervisor/backend.err.log | grep "AI Response"</code></li>
              <li>MongoDB'de tutarları kontrol et: <code className="bg-gray-200 px-1">amount_cents</code> field var mı?</li>
              <li>Frontend console'da <code className="bg-gray-200 px-1">amount_display</code> gelip gelmediğini kontrol et</li>
              <li>Rule engine logları: <code className="bg-gray-200 px-1">grep "Payment detected" backend.err.log</code></li>
            </ul>
          </div>

          <div className="text-xs text-gray-500 mt-4 pt-4 border-t">
            <p><strong>Tarih:</strong> 21 Ekim 2025</p>
            <p><strong>Durum:</strong> ✅ Çözüldü ve Test Edildi</p>
            <p><strong>Etkilenen Modüller:</strong> Credit Card Management, Statement Parsing, Payment Detection</p>
          </div>
        </div>
      )
    },

    {
      id: 'dev-case-2',
      category: 'developments',
      title: '🎯 CASE 2: Banka-Bağımsız Evrensel Ödeme Tespiti (Universal Payment Detection)',
      description: 'Tüm bankalar için (Kuveyt, QNB, Garanti, İş Bankası) çalışan evrensel ödeme tespit sistemi.',
      icon: ActivityIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-500',
      content: (
        <div className="text-sm text-gray-700 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="font-bold text-blue-800">🎯 AMAÇ</p>
            <p className="mt-2">Banka-agnostik (banka bağımsız), evrensel ödeme tespit sistemi oluşturmak. Her banka için ayrı kural yerine tek bir sistem ile tüm bankaları desteklemek.</p>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">✨ YENİ ÖZELLİKLER</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Payment Subtypes:</strong> CASH, AUTO_DEBIT, ACCOUNT_TRANSFER, POINTS, OTHER_CREDIT</li>
              <li><strong>Garanti "+" işareti:</strong> Tutar sonundaki "+" işaretini ödeme olarak algılar</li>
              <li><strong>Yanlış pozitif engelleme:</strong> DEVİR/FAİZ/ÜCRET/VERGİ/REFUND/CHARGEBACK hariç tutulur</li>
              <li><strong>İşaret analizi:</strong> Başta/sonda +/- işaretlerini analiz eder</li>
              <li><strong>Gevşek eşleşme:</strong> "ödeme" + "teşekkür" kombinasyonlarını yakalar</li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">🔍 TESPİT KURALLARI</p>
            <div className="space-y-3 ml-4">
              
              <div className="bg-red-50 p-2 rounded">
                <p className="font-semibold text-red-900">A) Hariç Tutulanlar (Yanlış Pozitif Engelleme)</p>
                <p className="text-xs mt-1 ml-4">devir, devreden, faiz, ücret, komisyon, vergi, bsmv, kkdf</p>
              </div>

              <div className="bg-green-50 p-2 rounded">
                <p className="font-semibold text-green-900">B) Ödeme Anahtar Kelimeleri</p>
                <ul className="text-xs mt-1 ml-4 space-y-1">
                  <li>• <strong>Kredi Kartı Borç Ödeme</strong> → CASH</li>
                  <li>• <strong>Ödemeniz için teşekkür ederiz / Payment received</strong> → CASH</li>
                  <li>• <strong>Hesaptan aktarım / Virman</strong> → ACCOUNT_TRANSFER</li>
                  <li>• <strong>Otomatik ödeme / Auto payment / Direct debit</strong> → AUTO_DEBIT</li>
                  <li>• <strong>Puan ile borç kapama / MaxiPuan / Miles / Points redeem</strong> → POINTS</li>
                </ul>
              </div>

              <div className="bg-yellow-50 p-2 rounded">
                <p className="font-semibold text-yellow-900">C) İşaret Analizi</p>
                <ul className="text-xs mt-1 ml-4 space-y-1">
                  <li>• <strong>Başta/sonda "-"</strong> → CASH (ödeme)</li>
                  <li>• <strong>Başta/sonda "+"</strong> → OTHER_CREDIT (Garanti tipi alacak)</li>
                  <li>• <strong>Refund/İade/Chargeback</strong> → HARIÇ (ödeme değil)</li>
                </ul>
              </div>

              <div className="bg-purple-50 p-2 rounded">
                <p className="font-semibold text-purple-900">D) Gevşek Eşleşme</p>
                <p className="text-xs mt-1 ml-4">"ödeme" VE "teşekkür" kelimeleri aynı satırda → CASH</p>
              </div>

            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">💻 KOD YAPISI</p>
            <div className="bg-gray-50 p-3 rounded text-xs font-mono space-y-2">
              <p><strong>Backend Fonksiyon:</strong></p>
              <p className="ml-4 text-green-700">detect_payment_universal(description, amount_text)</p>
              <p className="mt-2"><strong>Return:</strong></p>
              <div className="ml-4 bg-white p-2 rounded">
                {`{
  'is_payment': bool,
  'subtype': 'CASH' | 'AUTO_DEBIT' | 'ACCOUNT_TRANSFER' | 'POINTS' | 'OTHER_CREDIT',
  'reason': str  # debug için
}`}
              </div>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">📊 ÖRNEK SONUÇLAR</p>
            <div className="space-y-2 ml-4 text-xs">
              <div className="bg-white p-2 border-l-2 border-green-500">
                <p><strong>Input:</strong> "ÖDEMENİZ İÇİN TEŞEKKÜR EDERİZ" + "172.928,78+"</p>
                <p className="text-green-700"><strong>Output:</strong> is_payment=True, subtype='OTHER_CREDIT', reason='Trailing/leading plus'</p>
              </div>
              <div className="bg-white p-2 border-l-2 border-green-500">
                <p><strong>Input:</strong> "Kredi Kartı Borç Ödeme" + "-9.833,59"</p>
                <p className="text-green-700"><strong>Output:</strong> is_payment=True, subtype='CASH', reason='TR "Kredi Kartı Borç Ödeme"'</p>
              </div>
              <div className="bg-white p-2 border-l-2 border-green-500">
                <p><strong>Input:</strong> "Hesaptan Aktarım" + "50.000,00"</p>
                <p className="text-green-700"><strong>Output:</strong> is_payment=True, subtype='ACCOUNT_TRANSFER', reason='Account transfer'</p>
              </div>
              <div className="bg-white p-2 border-l-2 border-green-500">
                <p><strong>Input:</strong> "MaxiPuan ile Borç Kapama" + "15.000,00"</p>
                <p className="text-green-700"><strong>Output:</strong> is_payment=True, subtype='POINTS', reason='Points redemption'</p>
              </div>
              <div className="bg-white p-2 border-l-2 border-red-500">
                <p><strong>Input:</strong> "Devreden Bakiye" + "12.345,67"</p>
                <p className="text-red-700"><strong>Output:</strong> is_payment=False, reason='Carry-over / fee / tax'</p>
              </div>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">🏦 DESTEKLENEN BANKALAR</p>
            <div className="grid grid-cols-2 gap-2 ml-4 text-xs">
              <div className="bg-white p-2 rounded border">✅ Garanti BBVA</div>
              <div className="bg-white p-2 rounded border">✅ Kuveyt Türk</div>
              <div className="bg-white p-2 rounded border">✅ İş Bankası</div>
              <div className="bg-white p-2 rounded border">✅ QNB Finansbank</div>
              <div className="bg-white p-2 rounded border">✅ Yapı Kredi</div>
              <div className="bg-white p-2 rounded border">✅ Akbank</div>
              <div className="bg-white p-2 rounded border">✅ Ziraat Bankası</div>
              <div className="bg-white p-2 rounded border">✅ Halkbank</div>
              <div className="bg-white p-2 rounded border">✅ Denizbank</div>
              <div className="bg-white p-2 rounded border">✅ TEB</div>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">📂 DEĞİŞEN DOSYALAR</p>
            <div className="bg-gray-50 p-3 rounded text-xs font-mono space-y-1">
              <p>✅ /app/backend/server.py (detect_payment_universal)</p>
              <p>✅ Transaction processing updated (payment_subtype field added)</p>
              <p>✅ MongoDB schema (+payment_subtype, +payment_reason)</p>
            </div>
          </div>

          <div>
            <p className="font-bold text-gray-900 mb-2">🎯 AVANTAJLAR</p>
            <div className="bg-green-100 border-l-4 border-green-500 p-3 rounded">
              <ul className="list-disc list-inside space-y-1">
                <li>✅ Banka-bağımsız (tek kod, tüm bankalar)</li>
                <li>✅ Payment subtypes ile detaylı analiz</li>
                <li>✅ Garanti "+" işareti desteği</li>
                <li>✅ Yanlış pozitif engelleme</li>
                <li>✅ Debug-friendly (reason field)</li>
                <li>✅ Kolay genişletilebilir</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="font-bold text-blue-800">💡 KULLANIM</p>
            <div className="mt-2 text-xs font-mono bg-white p-2 rounded">
              {`# Backend'de otomatik çalışıyor
payment_result = detect_payment_universal(
    description="ÖDEMENİZ İÇİN TEŞEKKÜR EDERİZ",
    amount_text="172.928,78+"
)

# Frontend'de filtreleme
const payments = transactions.filter(t => t.type === "payment");
const cashPayments = transactions.filter(t => t.payment_subtype === "CASH");`}
            </div>
          </div>

          <div className="text-xs text-gray-500 mt-4 pt-4 border-t">
            <p><strong>Tarih:</strong> 21 Ekim 2025</p>
            <p><strong>Durum:</strong> ✅ Uygulandı ve Aktif</p>
            <p><strong>İlişkili Case:</strong> Case 1 (Cents-based storage ile birlikte çalışır)</p>
          </div>
        </div>
      )
    },

    // ═══════════════════════════════════════════════════════════════════
    // ACCOUNT MANAGEMENT RULES
    // ═══════════════════════════════════════════════════════════════════
    // Account Management Rules
    {
      id: 'rule-account-1',
      category: 'account',
      title: 'Tenant (Kiracı) Silme Kontrolü',
      description: 'Bir tenant sistemden silinmek istendiğinde, o tenant\'ın altında aktif kullanıcı olup olmadığı kontrol edilir.',
      icon: TrashIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Koşul:</strong> Bir tenant sistemden silinmek istendiğinde, o tenant\'ın altında aktif kullanıcı olup olmadığı kontrol edilir.</p>
          <p><strong>Sonuç:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Eğer tenant\'ın altında <strong>aktif kullanıcı varsa</strong>, tenant silinemez.</li>
            <li>Sistem kullanıcıya bir hata mesajı gösterir: "Bu tenant silinemez çünkü aktif kullanıcıları var."</li>
            <li>Tenant\'ı silmek için önce tüm kullanıcıların silinmesi veya pasifleştirilmesi gerekir.</li>
          </ul>
          <p><strong>Teknik Detay:</strong> Backend\'de delete tenant endpoint\'inde aktif kullanıcı sayısı kontrol edilir. Count &gt; 0 ise HTTP 400 hatası döndürülür.</p>
        </div>
      )
    },
    {
      id: 'rule-account-2',
      category: 'account',
      title: 'Kullanıcı Kendi Hesabını Silme',
      description: 'Kullanıcı kendi hesabını güvenlik ayarlarından silebilir, ancak bu işlem geri alınamaz.',
      icon: UserIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Koşul:</strong> Kullanıcı "Güvenlik Ayarları" sayfasından hesabını silmek istediğinde.</p>
          <p><strong>Süreç:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Adım 1:</strong> Kullanıcı "Hesabımı Sil" butonuna tıklar</li>
            <li><strong>Adım 2:</strong> İlk onay modal\'ı açılır - kullanıcı sonuçları okur ve onaylar</li>
            <li><strong>Adım 3:</strong> İkinci onay modal\'ı açılır - kullanıcı şifresini girer</li>
            <li><strong>Adım 4:</strong> Şifre doğrulanır ve hesap kalıcı olarak silinir</li>
            <li><strong>Adım 5:</strong> Başarı mesajı gösterilir ve 2 saniye sonra logout yapılır</li>
          </ul>
          <p><strong>Önemli Not:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Silme işlemi geri alınamaz</li>
            <li>Tüm kullanıcı verileri silinir</li>
            <li>Hesap gerçekten silinene kadar logout yapılmaz</li>
            <li>Tüm işlem geri alınamaz ve kullanıcı bu konuda uyarılır</li>
          </ul>
          <p><strong>Teknik Detay:</strong> SecuritySettings.js\'te multi-step modal sistemi ile güvenli hesap silme akışı implement edilmiştir.</p>
        </div>
      )
    },
    {
      id: 'rule-account-3',
      category: 'account',
      title: 'Tenant (Şirket) Kendi Kendini Silme',
      description: 'Tenant admini şirketi ve tüm kullanıcıları kalıcı olarak silebilir.',
      icon: TrashIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Koşul:</strong> Tenant (şirket) admini "Genel Ayarlar" sayfasından şirketi silmek istediğinde.</p>
          <p><strong>Süreç:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Adım 1:</strong> Admin "Şirketi Sil" butonuna tıklar</li>
            <li><strong>Adım 2:</strong> İlk onay modal\'ı - sonuçlar açıklanır (tüm kullanıcılar, veriler silinir)</li>
            <li><strong>Adım 3:</strong> İkinci onay modal\'ı - admin şifresini girer</li>
            <li><strong>Adım 4:</strong> Şifre doğrulanır</li>
            <li><strong>Adım 5:</strong> Şirket, tüm kullanıcıları ve verileri kalıcı olarak silinir</li>
            <li><strong>Adım 6:</strong> Başarı mesajı ve logout</li>
          </ul>
          <p><strong>Silinecek Veriler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Company/Tenant kaydı</li>
            <li>Tüm kullanıcılar (şirket altındaki)</li>
            <li>Tüm avans talepleri</li>
            <li>Tüm ayarlar ve konfigürasyonlar</li>
            <li>İlişkili tüm veriler</li>
          </ul>
          <p><strong>Teknik Detay:</strong> GeneralSettings.js\'te multi-step confirmation flow ile implement edilmiştir. Backend\'de cascade delete yapılır.</p>
        </div>
      )
    },
    {
      id: 'rule-account-4',
      category: 'account',
      title: 'Son Kullanıcı Silme - Otomatik Şirket Silme',
      description: 'Şirketteki son kullanıcı hesabını sildiğinde, şirket hesabı da otomatik olarak silinir.',
      icon: AlertTriangleIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Koşul:</strong> Bir kullanıcı kendi hesabını silmek istediğinde ve şirkette tek kullanıcı olduğunda.</p>
          <p><strong>Özel Uyarı Süreci:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Adım 1:</strong> Sistem otomatik olarak kullanıcı sayısını kontrol eder</li>
            <li><strong>Adım 2:</strong> Eğer son kullanıcıysa, normal silme akışından farklı özel bir uyarı gösterilir</li>
            <li><strong>Adım 3:</strong> "KRİTİK UYARI" modal\'ı açılır</li>
            <li><strong>Adım 4:</strong> Kullanıcıya şirket hesabının da silineceği detaylı olarak açıklanır</li>
            <li><strong>Adım 5:</strong> Kullanıcıdan tekrar şifre doğrulaması istenir</li>
            <li><strong>Adım 6:</strong> Şifre yanlışsa: "Girmiş olduğunuz şifre hatalıdır. Lütfen doğru şifrenizi giriniz."</li>
            <li><strong>Adım 7:</strong> Şifre doğruysa final onay ve 3 saniye sayaç</li>
            <li><strong>Adım 8:</strong> Hem kullanıcı hem de şirket hesabı silinir</li>
          </ul>
          <p><strong>Uyarı Modal İçeriği:</strong></p>
          <div className="bg-red-50 border border-red-200 rounded p-3 my-2">
            <ul className="list-disc list-inside space-y-1 ml-4 text-red-800">
              <li>Şirket hesabınız <strong>kalıcı olarak</strong> sistemimizden kaldırılacaktır</li>
              <li>Tüm kullanıcı kayıtlarınız <strong>kalıcı olarak silinecektir</strong></li>
              <li>Aboneliğiniz <strong>otomatik olarak sonlandırılacaktır</strong></li>
              <li>Tüm avans talepleri ve raporlarınız <strong>silinecektir</strong></li>
              <li>Bu işlem <strong>GERİ ALINAMAZ</strong></li>
              <li>Bu hesaba daha sonra <strong>hiçbir şekilde erişemeyeceksiniz</strong></li>
            </ul>
          </div>
          <p><strong>Butonlar:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Vazgeç:</strong> Tüm silme işlemini iptal eder, modal\'ları kapatır</li>
            <li><strong>Evet, Onaylıyorum:</strong> Şifre doğrulaması yaptıktan sonra işleme devam eder</li>
          </ul>
          <p><strong>Şifre Doğrulama:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Kullanıcı şifresini girer</li>
            <li>Backend change-password endpoint\'i ile şifre doğrulanır</li>
            <li>Hatalı şifre girilirse kırmızı hata mesajı gösterilir</li>
            <li>Kullanıcı istediği kadar deneme yapabilir</li>
            <li>Doğru şifre girilince işlem devam eder</li>
          </ul>
          <p><strong>Teknik Detay:</strong> SecuritySettings.js\'te `checkLastUser()` ile company stats\'tan kullanıcı sayısı kontrol edilir. `isLastUser === true` ise `companyDeleteWarning` modal\'ı gösterilir. Password verification için existing change-password endpoint kullanılır.</p>
          <p><strong>Güvenlik:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Çift şifre doğrulaması (ilk onay + kritik uyarı)</li>
            <li>Detaylı bilgilendirme ve onay sistemi</li>
            <li>Geri alınamaz işlem uyarısı</li>
            <li>3 saniyelik final sayaç</li>
          </ul>
        </div>
      )
    },

    // Security Rules
    {
      id: 'rule-security-1',
      category: 'security',
      title: 'Ultra Admin Şifre Görüntüleme ve Sıfırlama',
      description: 'Ultra Admin kullanıcı şifrelerini görüntüleyebilir ve sıfırlayabilir.',
      icon: KeyIcon,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-l-blue-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Yetki:</strong> Sadece Ultra Admin bu işlemi yapabilir.</p>
          <p><strong>Süreç:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Ultra Admin, kullanıcı listesinde "Şifre Göster" butonuna tıklar</li>
            <li>Backend yeni 8 karakterlik rastgele şifre oluşturur</li>
            <li>Kullanıcının şifresi bu yeni şifre ile güncellenir</li>
            <li>Yeni şifre Ultra Admin\'e gösterilir ve kopyalama imkanı sunulur</li>
            <li>Kullanıcı eski şifresi ile artık giriş yapamaz, yeni şifreyi kullanmalıdır</li>
          </ul>
          <p><strong>Güvenlik:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Her "Şifre Göster" tıklaması yeni şifre oluşturur</li>
            <li>Şifreler SHA-256 ile hash\'lenir</li>
            <li>Audit log\'a kaydedilir</li>
          </ul>
          <p><strong>Teknik Detay:</strong> POST /api/ultra-admin/users/:user_id/reset-password endpoint\'i kullanılır.</p>
        </div>
      )
    },
    {
      id: 'rule-security-2',
      category: 'security',
      title: 'Kullanıcı Hesabına Geçiş (Impersonation)',
      description: 'Ultra Admin şifre doğrulaması ile herhangi bir kullanıcı hesabına geçiş yapabilir.',
      icon: UserIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-l-purple-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Yetki:</strong> Sadece Ultra Admin bu işlemi yapabilir.</p>
          <p><strong>Süreç:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Ultra Admin kullanıcı kartında "Geçiş" butonuna tıklar</li>
            <li>Modal açılır ve Ultra Admin şifresi istenir</li>
            <li>Şifre backend\'de doğrulanır</li>
            <li>Başarılı olursa hedef kullanıcı için JWT token oluşturulur</li>
            <li>Yeni sekmede kullanıcı hesabına otomatik giriş yapılır</li>
          </ul>
          <p><strong>Güvenlik Önlemleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Her geçiş için Ultra Admin şifresi zorunlu</li>
            <li>Tüm geçişler audit log\'a kaydedilir</li>
            <li>Pasif kullanıcılara geçiş yapılamaz</li>
            <li>Token 8 saat geçerli</li>
            <li>Ultra Admin oturumu korunur (yeni sekmede açılır)</li>
          </ul>
          <p><strong>Teknik Detay:</strong> POST /api/ultra-admin/impersonate endpoint\'i kullanılır. Audit log\'da performed_by, target_user, timestamp kaydedilir.</p>
        </div>
      )
    },

    // Invite System Rules
    {
      id: 'rule-invite-1',
      category: 'invite',
      title: 'Davet Link Geçerlilik Süresi',
      description: 'Gönderilen davet linkleri 21 gün geçerlidir.',
      icon: MailIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-l-purple-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Süre:</strong> Gönderilen davet linkleri 21 gün geçerlidir.</p>
          <p><strong>Sonuç:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>21 gün sonunda davet linki otomatik olarak deaktif edilir</li>
            <li>Süresi geçmiş linke tıklandığında özel bir bilgilendirme sayfası gösterilir</li>
            <li>Kullanıcı web sitesinden normal kayıt yaparak 14 günlük ücretsiz deneme alabilir</li>
            <li>Davet listesinde kalan gün sayısı gösterilir</li>
          </ul>
          <p><strong>Teknik Detay:</strong> invited_at tarihinden itibaren 21 gün kontrolü yapılır. Expired davetler için /invite-expired sayfası gösterilir.</p>
        </div>
      )
    },
    {
      id: 'rule-invite-2',
      category: 'invite',
      title: '7. Gün Hatırlatma Maili',
      description: 'Davet edilen kullanıcı 7 gün içinde kayıt olmamışsa hatırlatma maili gönderilir.',
      icon: MailIcon,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-l-blue-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Koşul:</strong> Davet edilen kullanıcı 7 gün içinde kayıt olmamışsa.</p>
          <p><strong>İçerik:</strong> Davet bilgilendirme ve platform tanıtımı içeren dostça hatırlatma maili</p>
          <p><strong>Mesaj Tonu:</strong> "Sizi [tarih] tarihinde davet etmiştik. Sizi platformumuzda görmeyi sabırsızlıkla bekliyoruz."</p>
          <p><strong>Teknik Detay:</strong> Backend\'de scheduled job ile günlük kontrol. Status "pending" olan ve (current_date - invited_at) = 7 gün olan davetler için email gönderilir.</p>
        </div>
      )
    },
    {
      id: 'rule-invite-3',
      category: 'invite',
      title: '14. Gün Son Uyarı Maili',
      description: 'Davet edilen kullanıcı 14 gün içinde kayıt olmamışsa son uyarı maili gönderilir.',
      icon: AlertTriangleIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Koşul:</strong> Davet edilen kullanıcı 14 gün içinde kayıt olmamışsa.</p>
          <p><strong>İçerik:</strong> Davetin son 7 gün içinde geçersiz olacağına dair aciliyet içeren uyarı maili</p>
          <p><strong>Mesaj Tonu:</strong> "Sizi [tarih] tarihinde davet etmiştik. Sizi platformumuzda görmeyi sabırsızlıkla bekliyoruz. Ancak 7 gün içerisinde ücretsiz ve kredi kartı gerektirmeyen üyeliğinizi oluşturmazsanız, üyelik davetiniz iptal edilecektir."</p>
          <p><strong>Vurgu:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Son 7 gün kaldığı vurgulanır</li>
            <li>Ücretsiz deneme ve kredi kartı gerekmediği belirtilir</li>
            <li>CTA butonu ile hızlı kayıt yönlendirilir</li>
          </ul>
          <p><strong>Teknik Detay:</strong> (current_date - invited_at) = 14 gün kontrolü ile otomatik gönderim.</p>
        </div>
      )
    },
    {
      id: 'rule-invite-4',
      category: 'invite',
      title: 'Kalan Gün Göstergesi',
      description: 'Davet listesinde her davetin kalan günü renk kodlu badge ile gösterilir.',
      icon: UserIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Konum:</strong> Ultra Admin - Kiracı Yönetimi - "Davet Edilenler" tab\'ı</p>
          <p><strong>Gösterim:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>21-8 gün:</strong> 🟢 Yeşil badge - "X gün kaldı"</li>
            <li><strong>7-4 gün:</strong> 🟡 Sarı badge - "X gün kaldı"</li>
            <li><strong>3-0 gün:</strong> 🔴 Kırmızı badge - "X gün kaldı" (acil)</li>
            <li><strong>Süresi geçmiş:</strong> ⚪ Gri badge - "Süresi doldu"</li>
          </ul>
          <p><strong>Hesaplama:</strong> remaining_days = 21 - (current_date - invited_at).days</p>
          <p><strong>Teknik Detay:</strong> Frontend\'de real-time hesaplama veya backend\'den remaining_days field\'i döndürülür.</p>
        </div>
      )
    },
    {
      id: 'rule-invite-5',
      category: 'invite',
      title: 'İptal Edilmiş Davet Linki Yönlendirmesi',
      description: 'İptal edilmiş bir davetin linkine tıklandığında kullanıcı özel bir bilgilendirme sayfasına yönlendirilir.',
      icon: XIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Senaryo:</strong> Ultra Admin bir daveti iptal eder ve kişi daha önce gönderilen maildeki linke tıklar.</p>
          <p><strong>Davranış:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Kontrol:</strong> Sayfa yüklendiğinde backend ile invite durumu kontrol edilir (<code>/api/auth/check-invite/{'{invite_id}'}</code>)</li>
            <li><strong>İptal Edilmiş:</strong> Kullanıcı <code>/invite-cancelled</code> sayfasına yönlendirilir</li>
            <li><strong>Süresi Dolmuş:</strong> Hata mesajı gösterilir: "Bu davet süresi dolmuş"</li>
            <li><strong>Kullanılmış:</strong> Hata mesajı gösterilir: "Bu davet zaten kullanılmış"</li>
          </ul>
          <p><strong>İptal Sayfası İçeriği:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>📧 Davetin iptal edildiği bilgisi</li>
            <li>✉️ Yeniden davet edilirse email alacağı bilgisi</li>
            <li>🎯 İsterse kendisi abonelik başlatabilir (buton)</li>
            <li>🔗 "Abonelik Başlat" butonu kayıt sayfasına yönlendirir</li>
          </ul>
          <p><strong>Teknik Detay:</strong> RegisterPage.js useEffect içinde invite token kontrolü yapılır. Backend invite status'e göre uygun yönlendirme yapılır.</p>
          <p><strong>Amaç:</strong> Kullanıcıya profesyonel bir geri bildirim vermek ve alternatif çözüm sunmak.</p>
        </div>
      )
    },

    // Dashboard Rules
    {
      id: 'rule-dashboard-1',
      category: 'dashboard',
      title: 'Son Aktiviteler Bölümü',
      description: 'Dashboard\'da kullanıcının şirketine ait son 3 aktivite gösterilir. Aktivite yoksa bilgilendirme mesajı görüntülenir.',
      icon: SettingsIcon,
      iconColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-l-teal-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Genel Bakış:</strong> Dashboard ana sayfasında "Son Aktiviteler" bölümü, kullanıcının şirketine ait en son gerçekleşen 3 aktiviteyi görüntüler.</p>
          
          <p><strong>Aktivite Olmadığında:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>📭 <strong>Başlık:</strong> "Henüz gerçekleştirilmiş bir aktiviteniz yoktur"</li>
            <li>📝 <strong>Alt Mesaj:</strong> "Yapacağınız son işlemler burada sergilenecektir"</li>
            <li>💡 <strong>Icon:</strong> MessageSquareIcon gri renkte gösterilir</li>
          </ul>

          <p><strong>Aktiviteler Varsa:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>👤 Kullanıcı adı ve yapılan işlem açıklaması</li>
            <li>⏰ İşlemin yapıldığı zaman (örn: "2 saat önce", "1 gün önce")</li>
            <li>🎨 İşlem türüne göre renkli nokta:
              <ul className="list-none ml-4 mt-1">
                <li>• Mavi: Yeni talep/işlem</li>
                <li>• Yeşil: Onaylama/başarılı işlem</li>
                <li>• Kırmızı: Red/iptal işlemi</li>
                <li>• Gri: Diğer işlemler</li>
              </ul>
            </li>
          </ul>

          <p><strong>Backend API:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>GET /api/activities/recent?limit=3</code></li>
            <li>Kullanıcının company_id'sine göre en son advance_requests kayıtları getirilir</li>
            <li>Zaman hesaplaması: dakika, saat veya gün olarak otomatik formatlanır</li>
          </ul>

          <p><strong>Frontend Implementasyon:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Dosya:</strong> Dashboard.js</li>
            <li><strong>State:</strong> <code>recentActivities</code>, <code>activitiesLoading</code></li>
            <li><strong>useEffect:</strong> Component mount olduğunda aktiviteler API'den çekilir</li>
            <li><strong>Koşullu Rendering:</strong>
              <ul className="list-none ml-4 mt-1">
                <li>1. Loading state: Spinner gösterilir</li>
                <li>2. Aktivite yok: Bilgilendirme mesajı gösterilir</li>
                <li>3. Aktivite var: Liste halinde gösterilir (son 3 adet)</li>
              </ul>
            </li>
          </ul>

          <p><strong>Aktivite Tipleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>pending</code>: "yeni avans talebi oluşturdu" (mavi)</li>
            <li><code>approved</code>: "avans talebini onayladı" (yeşil)</li>
            <li><code>finance_approved</code>: "avans talebini finans onayladı" (yeşil)</li>
            <li><code>rejected</code>: "avans talebini reddetti" (kırmızı)</li>
            <li><code>closed</code>: "avans kapama yaptı" (gri)</li>
          </ul>

          <p><strong>Amaç:</strong> Kullanıcıya şirketteki son aktiviteler hakkında hızlı bir özet sunmak ve platformun aktif kullanımını teşvik etmek.</p>
        </div>
      )
    },


    // Subscription Management Rules
    {
      id: 'rule-subscription-0',
      category: 'subscription',
      title: 'Abonelik Tanımları',
      description: 'Plan türleri, tarihler, durumlar ve temel kavramlar',
      icon: CreditCardIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Plan Türleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>free_trial</code>: Ücretsiz deneme (14 gün + 5 kullanıcı)</li>
            <li><code>paid_monthly</code>: Ücretli aylık (30 gün)</li>
            <li><code>paid_annual</code>: Ücretli yıllık (365 gün)</li>
          </ul>
          <p><strong>Tarih Alanları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>start_at</code>: Abonelik başlangıç tarihi</li>
            <li><code>end_at</code>: Abonelik bitiş tarihi</li>
            <li><code>grace_end_at</code>: Ek deneme süresi bitiş tarihi (+7 gün)</li>
          </ul>
          <p><strong>Durum Değerleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>active</code>: Aktif abonelik</li>
            <li><code>grace</code>: Ek deneme süresinde</li>
            <li><code>expired</code>: Süresi dolmuş</li>
            <li><code>passive</code>: Pasif (90 gün tutulur)</li>
            <li><code>deleted</code>: Kalıcı silinmiş</li>
          </ul>
          <p><strong>Erişim Kontrolleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>is_active</code>: Boolean (true/false)</li>
            <li><code>seats_included</code>: Dahil kullanıcı sayısı (varsayılan: 5)</li>
            <li><code>owner_user_id</code>: Süper admin kullanıcı ID'si</li>
          </ul>
          <p><strong>Yardımcı Alanlar:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>has_any_activity</code>: Kullanıcı aktivitesi var mı?</li>
            <li><code>upgrade_url</code>: Abonelik yükseltme bağlantısı</li>
          </ul>
        </div>
      )
    },
    {
      id: 'rule-subscription-1',
      category: 'subscription',
      title: 'E-posta Olayları (E1-E8)',
      description: 'Abonelik sürecinde gönderilen otomatik e-postalar',
      icon: MailIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Genel Kurallar:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Tüm e-postalar süper admin'e gönderilir</li>
            <li>Gerçek tarihler kullanılır</li>
            <li>Butonlar: "Hemen Yükselt" veya "Hemen Devam et veya Yükselt"</li>
          </ul>
          <p><strong>E-posta Listesi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>E1 - Hoş geldin:</strong> Abonelik oluşturulduğunda hemen</li>
            <li><strong>E2 - Hatırlatma:</strong> 3. günde hiç aktivite yoksa</li>
            <li><strong>E3 - Teşekkür:</strong> 3. günde aktivite varsa</li>
            <li><strong>E4 - Son 3 Gün:</strong> Ücretsiz abonelik bitimine 3 gün kala</li>
            <li><strong>E5 - Son 3 Gün:</strong> Ücretli abonelik bitimine 3 gün kala</li>
            <li><strong>E6 - Bitiş +1 Saat:</strong> Ücretli abonelik bittiğinde (→ pasif, 90 gün)</li>
            <li><strong>E7 - Bitiş +1 Saat:</strong> Ücretsiz abonelik bittiğinde (+7 gün ek deneme)</li>
            <li><strong>E8 - Ek Deneme Bitiş +1 Saat:</strong> Ücretsiz ek süre bittiğinde (→ pasif)</li>
          </ul>
          <p><strong>Tetikleme Sistemi:</strong> APScheduler ile günlük kontrol</p>
        </div>
      )
    },
    {
      id: 'rule-subscription-2',
      category: 'subscription',
      title: 'Zamanlayıcılar ve Tetikleyiciler',
      description: 'Otomatik işlemler ve zamanlama kuralları',
      icon: ClockIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Tetikleme Zamanları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Abonelik oluşturulduğunda:</strong> E1 hemen gönder</li>
            <li><strong>3. gün kontrolü:</strong> Aktivite varsa E3, yoksa E2</li>
            <li><strong>Bitime 3 gün kala:</strong> Ücretsiz için E4, ücretli için E5</li>
            <li><strong>Bitiş +1 saat:</strong> Ücretli → E6, Ücretsiz → E7 (+7 gün ek)</li>
            <li><strong>Ek deneme bitişi +1 saat:</strong> E8 (pasife al)</li>
            <li><strong>Pasif +90 gün:</strong> Kalıcı silme kuyruğu başlat</li>
          </ul>
          <p><strong>Scheduler Implementation:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>APScheduler job: Her gün 09:00'da çalışır</li>
            <li>Tüm abonelikleri kontrol eder</li>
            <li>Tarihlere göre uygun email'leri tetikler</li>
            <li>Log kaydı tutar: <code>email_logs</code> collection</li>
          </ul>
        </div>
      )
    },
    {
      id: 'rule-subscription-3',
      category: 'subscription',
      title: 'Login Engelleme ve Yönlendirme',
      description: 'Expired/passive kullanıcılar için otomatik yönlendirme',
      icon: LockIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Kural:</strong> Durumu <code>expired</code>, <code>grace</code> sonrası, veya <code>passive</code> olan kullanıcı giriş yaptığında doğrudan "Abonelik Yükselt" sayfasına yönlendirilir.</p>
          <p><strong>Akış:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>1. Kullanıcı login yapar</li>
            <li>2. Backend company status'ünü kontrol eder</li>
            <li>3. Eğer <code>is_active: false</code> ise:</li>
            <ul className="list-none ml-6">
              <li>→ Frontend'e <code>subscription_expired: true</code> döner</li>
              <li>→ Frontend otomatik <code>/subscription-upgrade</code> sayfasına yönlendirir</li>
            </ul>
            <li>4. Kullanıcı ödeme yapar:</li>
            <ul className="list-none ml-6">
              <li>→ Company <code>is_active: true</code> olur</li>
              <li>→ Sayfa otomatik yenilenir</li>
              <li>→ Dashboard'a tam erişimle döner</li>
            </ul>
          </ul>
          <p><strong>Aktif kullanıcılar:</strong> Sistemi normal kullanabilir, engelleme yok</p>
          <p><strong>Implementation:</strong> <code>/api/auth/me</code> endpoint'i + Frontend AuthContext</p>
        </div>
      )
    },
    {
      id: 'rule-subscription-4',
      category: 'subscription',
      title: 'Paketler ve Limitler',
      description: 'Ücretsiz deneme ve ücretli plan detayları',
      icon: PackageIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Ücretsiz Deneme:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Süre: 14 gün</li>
            <li>Kullanıcı limiti: 5 kişi</li>
            <li>14. günde yükseltme yapılmazsa: +7 gün ek deneme verilir</li>
            <li>Ek süre bitiminde yükseltme yapılmazsa: Pasif (90 gün) statüsüne geçer</li>
          </ul>
          <p><strong>Ücretli Planlar:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Aylık:</strong> 30 gün, otomatik yenileme</li>
            <li><strong>Yıllık:</strong> 365 gün, otomatik yenileme</li>
            <li>Bitişte yenilenmezse: Pasif (90 gün) statüsüne geçer</li>
            <li>Kullanıcı limiti: Plana göre değişir (5, 10, 50, unlimited)</li>
          </ul>
          <p><strong>Yenileme Logic:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Stripe webhook ile otomatik yenileme</li>
            <li><code>subscription_end</code> tarihi güncellenir</li>
            <li><code>is_active: true</code> kalır</li>
          </ul>
        </div>
      )
    },
    {
      id: 'rule-subscription-5',
      category: 'subscription',
      title: 'Veri Saklama ve Silme',
      description: '90 gün pasif saklama ve kalıcı silme kuralları',
      icon: TrashIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Pasif Durumu:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Abonelik bittiğinde company <code>is_active: false</code> olur</li>
            <li><code>passive_since</code> = şu anki tarih</li>
            <li>Kullanıcılar login yapabilir ama sadece upgrade sayfasını görür</li>
            <li>Veriler 90 gün boyunca saklanır</li>
          </ul>
          <p><strong>Kalıcı Silme (90 gün sonra):</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Silinecek veriler:</strong></li>
            <ul className="list-none ml-6">
              <li>→ Company kaydı</li>
              <li>→ Tüm users kayıtları</li>
              <li>→ Advance requests</li>
              <li>→ Dosyalar ve loglar</li>
            </ul>
            <li>Company <code>is_deleted: true</code> işaretlenir</li>
            <li>Deleted tenants listesine taşınır</li>
          </ul>
          <p><strong>Scheduler:</strong> Her gün pasif +90 gün geçmiş kayıtları siler</p>
          <p><strong>Log:</strong> Silme işlemi <code>deletion_logs</code> collection'a kaydedilir</p>
        </div>
      )
    },
    {
      id: 'rule-subscription-6',
      category: 'subscription',
      title: 'Aktivite Kriteri',
      description: 'Kullanıcı aktivitesinin tanımı ve tespiti',
      icon: ActivityIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Aktivite Sayılan İşlemler:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Login yapma</li>
            <li>Avans talebi oluşturma</li>
            <li>Avans kapama</li>
            <li>Kullanıcı ekleme/düzenleme</li>
            <li>Profil güncelleme</li>
            <li>Herhangi bir API endpoint çağrısı (GET hariç)</li>
          </ul>
          <p><strong>Kayıt Sistemi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Company kaydında <code>last_activity_at</code> field'ı</li>
            <li>Her aktivite sonrası bu alan güncellenir</li>
            <li>3. gün kontrolünde bu tarih kontrol edilir</li>
          </ul>
          <p><strong>Email Tetikleme:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>last_activity_at</code> varsa → E3 (Teşekkür)</li>
            <li><code>last_activity_at</code> yoksa veya çok eski → E2 (Hatırlatma)</li>
          </ul>
        </div>
      )
    },
    {
      id: 'rule-subscription-7',
      category: 'subscription',
      title: 'Senaryolar (S1-S4)',
      description: 'Farklı abonelik akış senaryoları',
      icon: MapIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-3">
          <div className="border-l-4 border-blue-500 pl-3">
            <p><strong>S1: Ücretsiz + Aktivitesiz Kullanıcı</strong></p>
            <p className="text-xs text-gray-600">0. gün → E1 → 3. gün → E2 → 11. gün → E4 → 14. gün → E7 (+7 gün) → 21. gün → E8 → pasif → 90 gün → silme</p>
          </div>
          <div className="border-l-4 border-green-500 pl-3">
            <p><strong>S2: Ücretsiz + Aktif Kullanıcı</strong></p>
            <p className="text-xs text-gray-600">0. gün → E1 → 3. gün → E3 → 11. gün → E4 → 14. gün → E7 (+7 gün) → 21. gün → E8 → pasif → 90 gün → silme</p>
          </div>
          <div className="border-l-4 border-yellow-500 pl-3">
            <p><strong>S3: Ücretli (Yenilemeyen)</strong></p>
            <p className="text-xs text-gray-600">0. gün → E1 → 3. gün → (E2/E3) → bitiş-3 gün → E5 → bitiş → E6 → pasif → 90 gün → silme</p>
          </div>
          <div className="border-l-4 border-purple-500 pl-3">
            <p><strong>S4: Ücretli (Yenileyen)</strong></p>
            <p className="text-xs text-gray-600">0. gün → E1 → 3. gün → (E2/E3) → bitiş-3 gün → E5 → ödeme başarılı → <code>active</code> statü devam eder, yeni <code>end_at</code> tarihi atanır</p>
          </div>
        </div>
      )
    },
    {
      id: 'rule-subscription-8',
      category: 'subscription',
      title: 'Metin Tutarlılığı',
      description: 'E-posta ve UI buton metinleri',
      icon: TypeIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Standart Buton Metinleri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>"Hemen Yükselt" - Ücretsiz deneme ve grace period için</li>
            <li>"Hemen Devam et veya Yükselt" - Ücretli abonelik yenileme için</li>
          </ul>
          <p><strong>Email Subject Formats:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>E1: "Hoş geldiniz - [Company Name]"</li>
            <li>E2: "Platformunuzu keşfetmeye başlayın!"</li>
            <li>E3: "Teşekkürler - Harika başlangıç!"</li>
            <li>E4/E5: "Aboneliğiniz [X] gün sonra sona eriyor"</li>
            <li>E6/E8: "Aboneliğiniz sona erdi - Verileriniz 90 gün saklanacak"</li>
            <li>E7: "Özel hediye: +7 gün ek deneme süresi"</li>
          </ul>
          <p><strong>Tutarlılık Kuralı:</strong> Tüm email template'lerinde ve frontend mesajlarında aynı terimler kullanılmalı</p>
        </div>
      )
    },
    {
      id: 'rule-subscription-9',
      category: 'subscription',
      title: 'Gözlemlenebilirlik ve Raporlama',
      description: 'Log kayıtları ve Ultra Admin paneli raporları',
      icon: EyeIcon,
      iconColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-l-orange-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Log Kayıtları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Her kural çalıştığında log kaydı tutulur</li>
            <li>Log format: <code>{'{'}rule: "E1", date: "2025-10-18", target_user: "user@example.com", status: "sent"{'}'}</code></li>
            <li>Collection: <code>email_logs</code></li>
          </ul>
          <p><strong>Ultra Admin Raporu:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Her tenant için mevcut durum gösterilir</li>
            <li>Sıradaki e-posta tarihi ve türü</li>
            <li>Silme tarihi (pasif +90 gün)</li>
            <li>Son aktivite tarihi</li>
            <li>Toplam gönderilen e-posta sayısı</li>
          </ul>
          <p><strong>Dashboard Widget:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Yaklaşan abonelik bitişleri (7 gün içinde)</li>
            <li>Pasif tenant'lar ve silme tarihleri</li>
            <li>Bu hafta gönderilen e-postalar</li>
          </ul>
        </div>
      )
    },
    {
      id: 'rule-subscription-10',
      category: 'subscription',
      title: 'Ultra Admin Davet Sistemi',
      description: 'Davet geçerliliği, iptal ve otomatik temizleme kuralları',
      icon: SendIcon,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-l-purple-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Davet Geçerlilik Süresi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Ultra Admin kullanıcı davet edebilir</li>
            <li>Davet linki <strong>30 gün</strong> boyunca geçerlidir (eski: 90 gün)</li>
            <li>30 gün içinde kayıt olmazsa davet otomatik silinir</li>
            <li>Davet linki geçersiz hale gelir</li>
          </ul>
          <p><strong>Davetin İptali (Geri Çekme):</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Davet gönderildikten sonra Ultra Admin daveti iptal edebilir</li>
            <li>İptal edildiğinde link <strong>anında</strong> geçersiz olur</li>
            <li>Kullanıcı linke tıklarsa: <code>/invite-cancelled</code> sayfasına yönlendirilir</li>
            <li>Mesaj: "Davet linki iptal edilmiştir"</li>
          </ul>
          <p><strong>Davet Durumları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code>sent</code>: Gönderilmiş, beklemede</li>
            <li><code>accepted</code>: Kullanıcı kaydı tamamladı</li>
            <li><code>cancelled</code>: Ultra Admin tarafından iptal edildi</li>
            <li><code>expired</code>: 30 gün geçti, otomatik silindi</li>
          </ul>
          <p><strong>Otomatik Temizleme:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Scheduler job günlük çalışır (APScheduler)</li>
            <li>30 günden eski <code>sent</code> durumundaki davetleri bulur</li>
            <li>Status'ü <code>expired</code> yapar</li>
            <li>"Davet Edilenler" listesinden kaldırır</li>
          </ul>
          <p><strong>Email ile Company Eşleşmesi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Davet edilen email ile kayıtlı company varsa</li>
            <li>O davet otomatik <code>accepted</code> işaretlenir</li>
            <li>"Davet Edilenler" listesinden çıkar</li>
            <li>"Aktif Kiracılar" listesinde görünür</li>
          </ul>
          <p><strong>Log Kayıtları:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Davet gönderildiğinde: <code>invite_logs</code> collection</li>
            <li>Davet iptal edildiğinde: iptal tarihi ve sebep kaydedilir</li>
            <li>Davet kabul edildiğinde: kabul tarihi ve company_id kaydedilir</li>
          </ul>
        </div>
      )
    },

    // Numbering Rules
    {
      id: 'rule-numbering-1',
      category: 'numbering',
      title: 'Tenant Numaralandırma Sistemi',
      description: 'Her tenant\'a otomatik, benzersiz 6 haneli numara atanır.',
      icon: HashIcon,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-l-green-500',
      content: (
        <div className="text-sm text-gray-700 space-y-2">
          <p><strong>Format:</strong> 6 haneli, otomatik artan numara (100001, 100002, ...)</p>
          <p><strong>Uygulama:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Her yeni tenant kayıt olduğunda otomatik numara atanır</li>
            <li>Numara MongoDB counter collection ile yönetilir</li>
            <li>Mevcut tenant\'lara retroaktif olarak numara atanmıştır</li>
            <li>Numara benzersizdir ve değiştirilemez</li>
          </ul>
          <p><strong>Görünüm:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Ultra Admin tenant kartlarında ve listelerinde</li>
            <li>User dashboard header\'ında</li>
            <li>Destek formlarında (otomatik doldurulur)</li>
          </ul>
          <p><strong>Teknik Detay:</strong> Backend\'de get_next_tenant_number() fonksiyonu ile atomic increment. Company model\'inde tenant_number field\'i.</p>
        </div>
      )
    },

    // ═══════════════════════════════════════════════════════════════════
    // Credit Card Management Rules
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'creditcard_payment_detection',
      title: 'Kredi Kartı Ödeme Tespiti',
      category: 'creditcard',
      tags: ['kredi kartı', 'ödeme', 'parsing', 'AI', 'banka'],
      icon: CreditCardIcon,
      severity: 'critical',
      description: 'Farklı bankaların ekstre formatlarında ödeme işlemlerinin doğru tespit edilmesi kuralları',
      content: (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="font-bold text-red-800 mb-2">🚨 KRİTİK KURAL: TUTARA BAKMA, SADECE KEYWORD'E BAK!</p>
            <p className="text-red-700">Farklı bankalar ödemeleri farklı işaretlerle gösterir:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Kuveyt Türk:</strong> "Kredi Kartı Borç Ödeme" -6.249,43 TL (NEGATİF)</li>
              <li><strong>Garanti:</strong> "Ödemeniz için teşekkür ederiz" +30.000 TL (POZİTİF)</li>
            </ul>
            <p className="mt-2 font-semibold">→ Sistem sadece keyword'lere bakarak ödeme tespit eder, işaret ve tutarı göz ardı eder!</p>
          </div>
          
          <p><strong>Türkçe Ödeme Keyword'leri:</strong></p>
          <div className="bg-blue-50 p-3 rounded text-sm">
            <p className="mb-2"><strong>Temel:</strong> ödeme, teşekkür, borç ödeme, kart ödemesi</p>
            <p className="mb-2"><strong>Hesaptan:</strong> hesaptan ödeme, otomatik ödeme, otomatik tahsilat, hesaptan virman, vadesiz hesap ödeme</p>
            <p className="mb-2"><strong>Mobil/Online:</strong> mobil bankacılık ödeme, internet şube ödeme, online ödeme, cep şube ödeme</p>
            <p className="mb-2"><strong>Şube/Nakit:</strong> şubeden ödeme, nakit ödeme, gişeden ödeme, kasadan tahsilat</p>
            <p><strong>Kısmi:</strong> kısmi ödeme, asgari tutar ödeme, minimum ödeme, ekstre borç ödeme</p>
          </div>

          <p><strong>İngilizce Payment Keywords:</strong></p>
          <div className="bg-green-50 p-3 rounded text-sm">
            <p className="mb-2"><strong>Basic:</strong> payment, thank you payment, payment received, payment posted, payment processed</p>
            <p className="mb-2"><strong>Auto:</strong> auto payment, automatic payment, auto debit, direct debit</p>
            <p className="mb-2"><strong>Online/Mobile:</strong> mobile payment, internet banking payment, online banking payment, digital payment</p>
            <p className="mb-2"><strong>Branch:</strong> branch payment, cash payment, over counter payment</p>
            <p><strong>Partial:</strong> partial payment, minimum payment, installment payment</p>
          </div>

          <p><strong>AI Parsing Kuralı:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>AI, transaction description'ı aynen kopyalar (ödeme kelimelerini dahil eder)</li>
            <li>AI, ödeme tespit ettiğinde amount'ı POZİTİF döndürür</li>
            <li>Backend classify_and_fix() fonksiyonu keyword'e bakarak ödemeyi tespit eder</li>
            <li>Backend ödeme tespit ettiğinde amount'ı negatif yapar: -abs(amount)</li>
          </ul>

          <p><strong>Uygulama:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>parse_statement_with_ai() fonksiyonunda kapsamlı AI prompt</li>
            <li>classify_and_fix() fonksiyonunda 50+ keyword regex pattern</li>
            <li>Her ekstre parse'ı her banka formatına uyum sağlar</li>
          </ul>

          <p className="text-xs text-gray-600 mt-3"><strong>Teknik:</strong> /app/backend/server.py - PAYMENT_REGEX, classify_and_fix(), parse_statement_with_ai()</p>
        </div>
      )
    },

    {
      id: 'creditcard_statement_dates',
      title: 'Ekstre Tarih ve Tutar Yönetimi',
      category: 'creditcard',
      tags: ['ekstre', 'tarih', 'tutar', 'period'],
      icon: ClockIcon,
      severity: 'high',
      description: 'Ekstre dönem tarihlerinin ve tutarlarının doğru hesaplanması',
      content: (
        <div className="space-y-3">
          <p><strong>Ekstre Bilgileri:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>statement_period_start:</strong> Ekstre dönemi başlangıç (DD/MM/YYYY)</li>
            <li><strong>statement_period_end:</strong> Ekstre dönemi bitiş (DD/MM/YYYY)</li>
            <li><strong>billing_date:</strong> Hesap kesim tarihi</li>
            <li><strong>payment_due_date:</strong> Son ödeme tarihi</li>
            <li><strong>statement_amount:</strong> Sadece giderlerin toplamı (ödemeler dahil DEĞİL)</li>
          </ul>

          <p><strong>statement_amount Hesaplama:</strong></p>
          <div className="bg-yellow-50 p-3 rounded">
            <code className="text-sm">
              statement_amount = SUM(transactions WHERE amount {'>'} 0)
            </code>
            <p className="mt-2 text-sm">Sadece pozitif tutarlar (giderler) toplanır. Negatif tutarlar (ödemeler) hesaba katılmaz.</p>
          </div>

          <p><strong>Frontend Ay Gösterimi:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>getStatementMonth() fonksiyonu period_start ve period_end'den dominant ayı hesaplar</li>
            <li>Örnek: 08/08/2025 - 08/09/2025 → "AĞUSTOS 2025"</li>
            <li>Ay bilgisi büyük badge içinde ekstreler listesinde gösterilir</li>
          </ul>

          <p className="text-xs text-gray-600 mt-3"><strong>Teknik:</strong> Backend: GET /api/credit-cards/statements/{'{id}'}/report, Frontend: getStatementMonth() in CreditCardManagement.js</p>
        </div>
      )
    },

    {
      id: 'creditcard_transactions_display',
      title: 'Gider ve Ödeme Gösterimi',
      category: 'creditcard',
      tags: ['işlemler', 'gider', 'ödeme', 'görünüm'],
      icon: EyeIcon,
      severity: 'medium',
      description: 'İşlemler sekmesinde gider ve ödemelerin ayrı bölümlerde gösterilmesi',
      content: (
        <div className="space-y-3">
          <p><strong>İşlemler Sekmesi Yapısı:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><strong>Üst Bölüm:</strong> Tüm işlemler (gider ve ödemeler karışık) - editable table</li>
            <li><strong>Alt Bölüm:</strong> "BORÇ ÖDEMELERİ" - sadece ödemeler (amount {'<'} 0) - read-only</li>
          </ul>

          <p><strong>Filtreleme:</strong></p>
          <div className="bg-blue-50 p-3 rounded">
            <code className="text-sm">
              payments = localTransactions.filter(txn ={'>'} txn.amount {'<'} 0)
            </code>
          </div>

          <p><strong>Rapor Modalı:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Üstte 2 özet kartı: Devreden Bakiye, Dönem Borcu</li>
            <li>Ortada yan yana 2 büyük kart:
              <ul className="ml-6 mt-1">
                <li>🔴 GİDERLER (kırmızı tema) - scrollable list</li>
                <li>🟢 ÖDEMELER (yeşil tema) - scrollable list</li>
              </ul>
            </li>
            <li>API'den expenses ve payments array'leri ayrı gelir</li>
          </ul>

          <p className="text-xs text-gray-600 mt-3"><strong>Teknik:</strong> GET /api/credit-cards/statements/{'{id}'}/report döner: expenses[], payments[]</p>
        </div>
      )
    },

    // ═══════════════════════════════════════════════════════════════════
    // 👤 KULLANICI (USER)
    // ═══════════════════════════════════════════════════════════════════
    // 👤 KULLANICI (USER)
    // ═══════════════════════════════════════════════════════════════════
    {
      id: 'user-permanent-deletion',
      category: 'user',
      title: '🗑️ Kullanıcı Kalıcı Silme Yetkisi',
      description: 'Süper Adminler işlemi olmayan kullanıcıları kalıcı olarak silebilir',
      icon: TrashIcon,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      content: (
        <div className="text-sm text-gray-700 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="font-bold text-blue-800">ℹ️ KULLANICI SİLME YETKİSİ</p>
            <p className="mt-2">Süper Adminler, sistemde hiçbir işlemi olmayan kullanıcıları kalıcı olarak silebilir.</p>
          </div>

          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">✅</span>
                <p className="font-bold text-green-900">Silinebilir Kullanıcılar</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Hiç avans talebi oluşturmamış kullanıcılar</li>
                <li>Herhangi bir avans talebini onaylamamış kullanıcılar</li>
                <li>Başka kullanıcıların yöneticisi olmayan kullanıcılar</li>
                <li>Aktif işlemi olmayan (pending, approved, paid) kullanıcılar</li>
                <li><strong>Not:</strong> Süper Admin kullanıcıları silinemez</li>
              </ul>
            </div>

            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">❌</span>
                <p className="font-bold text-red-900">Silinemez Kullanıcılar</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Bekleyen veya ödeme bekleyen avans talebi olan kullanıcılar</li>
                <li>Başka kullanıcıların avanslarını onaylamış kullanıcılar</li>
                <li>Alt çalışanları olan (yönetici) kullanıcılar</li>
                <li>Süper Admin rolündeki kullanıcılar</li>
                <li><strong>Uyarı:</strong> Bu kullanıcılar için sadece "Pasif Yap" seçeneği kullanılabilir</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">⚙️</span>
                <p className="font-bold text-yellow-900">Silme İşlemi Süreci</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li><strong>Adım 1:</strong> Süper Admin "Kullanıcı Yönetimi" sayfasına gider</li>
                <li><strong>Adım 2:</strong> Kullanıcı kartındaki "Pasif Yap" butonunun yanındaki "Sil" butonuna tıklar</li>
                <li><strong>Adım 3:</strong> Sistem otomatik olarak kullanıcının işlemlerini kontrol eder</li>
                <li><strong>Adım 4:</strong> Eğer kullanıcının işlemi varsa, hata mesajı gösterilir</li>
                <li><strong>Adım 5:</strong> İşlem yoksa, onay modalı açılır ve uyarılar gösterilir</li>
                <li><strong>Adım 6:</strong> Admin onaylarsa, kullanıcı kalıcı olarak silinir ve arşive taşınır</li>
              </ul>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔒</span>
                <p className="font-bold text-purple-900">Güvenlik ve Arşivleme</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Silinen kullanıcı verileri <code>archived_users</code> koleksiyonuna taşınır</li>
                <li>Silme işlemi geri alınamaz (kalıcı silme)</li>
                <li>Arşivlenen veriler hangi admin tarafından silindiğini kaydeder</li>
                <li>Alt çalışanlar varsa, yöneticileri otomatik olarak temizlenir</li>
                <li><strong>Önemli:</strong> Kullanıcının eski talep ve onay geçmişi korunur</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded mt-4">
            <p className="font-bold text-green-800">✅ UYGULANAN ÇÖZÜM</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Backend:</strong> DELETE /api/users/{'{user_id}'}/permanent endpoint'i kullanılır</li>
              <li><strong>Kontrol:</strong> calculate_user_deletion_impact() fonksiyonu ile işlem kontrolü</li>
              <li><strong>Frontend:</strong> UserManagementSettings.js'de "Sil" butonu eklendi</li>
              <li><strong>Modal:</strong> Silme öncesi onay modalı ve etki raporu gösterimi</li>
              <li><strong>Arşivleme:</strong> ArchivedUser model ile veri korunması</li>
            </ul>
          </div>

          <div className="bg-gray-100 p-3 rounded mt-4">
            <p className="font-bold mb-2">🔧 TEKNİK DETAYLAR</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-xs">
              <li><strong>Backend Endpoint:</strong> DELETE /api/users/{'{user_id}'}/permanent</li>
              <li><strong>Impact Check:</strong> GET /api/users/{'{user_id}'}/deletion-impact</li>
              <li><strong>Dosya:</strong> /app/backend/server.py - satır ~2646</li>
              <li><strong>Fonksiyon:</strong> permanent_delete_user(), calculate_user_deletion_impact()</li>
              <li><strong>Koleksiyon:</strong> archived_users (arşivlenen kullanıcılar)</li>
              <li><strong>Frontend:</strong> /app/frontend/src/components/UserManagementSettings.js</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'user-position-hierarchy-approval',
      category: 'user',
      title: '🎯 Pozisyon Bazlı Onay Sistemi',
      description: 'Avans taleplerinde pozisyon hiyerarşisine göre otomatik onaylayıcı belirleme',
      icon: UserIcon,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-l-indigo-500',
      content: (
        <div className="text-sm text-gray-700 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
            <p className="font-bold text-blue-800">ℹ️ YÖNETİCİ YETKİLERİ - POZISYON BAZLI ONAY</p>
            <p className="mt-2">Bir kullanıcı avans talebi oluşturduğunda, sistem aşağıdaki sıraya göre onaylayıcı belirler:</p>
          </div>

          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">1️⃣</span>
                <p className="font-bold text-green-900">Pozisyon Bazlı Hiyerarşi (ÖNCELİK)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Kullanıcının pozisyonuna atanmış yönetici pozisyon var mı kontrol edilir</li>
                <li><strong>Örnek:</strong> "Proje Asistanı" pozisyonuna "Proje Yöneticisi" atandıysa</li>
                <li>O yönetici pozisyondaki <strong>aktif</strong> bir kullanıcı bulunur</li>
                <li>Onay otomatik olarak ona gider</li>
                <li><strong>Avantaj:</strong> Kullanıcı değişse bile pozisyon hiyerarşisi sabit kalır</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">2️⃣</span>
                <p className="font-bold text-yellow-900">Kullanıcı Bazlı Yönetici (İKİNCİL)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Pozisyon hiyerarşisi tanımlı değilse</li>
                <li>Kullanıcının direkt yöneticisine bakar (<code>user.manager_id</code>)</li>
                <li>Hiyerarşi yönetimi sayfasından manuel olarak atanmış yönetici</li>
                <li><strong>Not:</strong> Kullanıcı bazlı, pozisyondan bağımsız</li>
              </ul>
            </div>

            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">3️⃣</span>
                <p className="font-bold text-red-900">Süper Admin (FALLBACK)</p>
              </div>
              <ul className="list-disc list-inside space-y-1 ml-6 text-gray-700">
                <li>Ne pozisyon hiyerarşisi ne de kullanıcı yöneticisi varsa</li>
                <li>Şirketin <strong>Süper Admin</strong> kullanıcısına gider</li>
                <li>Son çare mekanizması - her durumda birisi onaylayabilsin</li>
              </ul>
            </div>
          </div>

          <div className="bg-purple-50 border-l-4 border-purple-400 p-3 rounded">
            <p className="font-bold text-purple-800">📋 ÖRNEK SENARYO</p>
            <div className="mt-2 space-y-2">
              <p><strong>Adım 1:</strong> Admin, "Proje Asistanı" pozisyonuna "Proje Yöneticisi"ni yönetici olarak atar</p>
              <p><strong>Adım 2:</strong> Ahmet (Proje Asistanı pozisyonunda) 5000 TL avans talebi oluşturur</p>
              <p><strong>Adım 3:</strong> Sistem şu kontrolü yapar:</p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Ahmet'in pozisyonu: "Proje Asistanı"</li>
                <li>Bu pozisyonun yöneticisi: "Proje Yöneticisi"</li>
                <li>"Proje Yöneticisi" pozisyonundaki aktif kullanıcı: Mehmet</li>
              </ul>
              <p><strong>Sonuç:</strong> ✅ Onay <strong>Mehmet</strong>'e gider (Proje Yöneticisi)</p>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded mt-4">
            <p className="font-bold text-green-800">✅ UYGULANAN ÇÖZÜM</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><strong>Backend:</strong> POST /api/advance-requests endpoint'i güncellendi</li>
              <li><strong>Öncelik sırası:</strong> Pozisyon hiyerarşisi → Kullanıcı yöneticisi → Süper Admin</li>
              <li><strong>Koleksiyon:</strong> <code>position_hierarchy</code> MongoDB collection</li>
              <li><strong>Alan:</strong> <code>position_id</code> ve <code>manager_position_id</code></li>
              <li><strong>Kontrol:</strong> Her avans talebinde otomatik kontrol edilir</li>
            </ul>
          </div>

          <div className="bg-gray-100 p-3 rounded mt-4">
            <p className="font-bold mb-2">🔧 TEKNİK DETAYLAR</p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-xs">
              <li><strong>Dosya:</strong> /app/backend/server.py</li>
              <li><strong>Fonksiyon:</strong> create_advance_request() - satır ~4611</li>
              <li><strong>Kontrol 1:</strong> position_hierarchy.find_one(position_id, company_id)</li>
              <li><strong>Kontrol 2:</strong> users.find_one(role_id=manager_position_id, is_active=True)</li>
              <li><strong>Fallback:</strong> current_user.manager_id veya Super Admin</li>
            </ul>
          </div>
        </div>
      )
    },
  ];


  // Toggle rule expansion
  const toggleRule = (ruleId) => {
    setExpandedRules(prev => ({
      ...prev,
      [ruleId]: !prev[ruleId]
    }));
  };

  // Expand all in category
  const expandAllInCategory = (categoryId) => {
    const categoryRules = filteredRules.filter(r => r.category === categoryId);
    const newExpanded = { ...expandedRules };
    categoryRules.forEach(rule => {
      newExpanded[rule.id] = true;
    });
    setExpandedRules(newExpanded);
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedRules({});
  };

  // Filter and search logic
  const filteredRules = useMemo(() => {
    let filtered = allRules;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(rule => rule.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(rule => 
        rule.title.toLowerCase().includes(term) ||
        rule.description.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [selectedCategory, searchTerm]);

  // Get stats
  const stats = {
    total: allRules.length,
    categories: categories.length - 1, // Exclude 'all'
    byCategory: categories.slice(1).map(cat => ({
      ...cat,
      count: allRules.filter(r => r.category === cat.id).length
    }))
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BookOpenIcon className="w-8 h-8 mr-3 text-blue-600" />
              Platform Kuralları Kütüphanesi
            </h2>
            <p className="text-gray-600 mt-1">Platform genelinde uygulanan iş kuralları ve politikalar</p>
          </div>
          <div className="flex space-x-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-xs text-gray-500">Toplam Kural</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.categories}</div>
              <div className="text-xs text-gray-500">Kategori</div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Kural ara... (örn: şifre, silme, davet)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-12 py-3 w-full text-lg"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Category Filter */}
        <div className="col-span-3">
          <Card className="p-4 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <FilterIcon className="w-4 h-4 mr-2" />
                Kategoriler
              </h3>
              <button
                onClick={collapseAll}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Tümünü Kapat
              </button>
            </div>
            <div className="space-y-2">
              {categories.map((category) => {
                const Icon = category.icon;
                const count = category.id === 'all' 
                  ? stats.total 
                  : stats.byCategory.find(c => c.id === category.id)?.count || 0;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{count}</Badge>
                  </button>
                );
              })}
            </div>

            {selectedCategory !== 'all' && (
              <button
                onClick={() => expandAllInCategory(selectedCategory)}
                className="w-full mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Tümünü Genişlet
              </button>
            )}
          </Card>
        </div>

        {/* Main Content - Rules List */}
        <div className="col-span-9">
          {filteredRules.length === 0 ? (
            <Card className="p-12 text-center">
              <SearchIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Kural Bulunamadı</h3>
              <p className="text-gray-600">
                {searchTerm ? `"${searchTerm}" için sonuç bulunamadı.` : 'Bu kategoride kural bulunmuyor.'}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredRules.map((rule) => {
                const Icon = rule.icon;
                const isExpanded = expandedRules[rule.id];

                return (
                  <Card key={rule.id} className={`border-l-4 ${rule.borderColor} ${rule.bgColor}`}>
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() => toggleRule(rule.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className={`w-10 h-10 rounded-lg ${rule.bgColor} flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`w-5 h-5 ${rule.iconColor}`} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{rule.title}</h4>
                            <p className="text-sm text-gray-600">{rule.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Badge className={categories.find(c => c.id === rule.category)?.color}>
                            {categories.find(c => c.id === rule.category)?.name}
                          </Badge>
                          {isExpanded ? (
                            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                        <div className="ml-14">
                          {rule.content}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UltraPlatformRules;
